import { createRouter, createWebHistory } from "vue-router";

import Home from "@/views/Home.vue";
import NotFound from "@/views/NotFound.vue";
import Login from "@/views/auth/Login.vue";
import { isFeatureEnabled, ensureFeatureFlags } from "@/config/featureFlags.js";

import salesRoutes from "./sales.js"
import reviewsRoutes from "./reviews.js"
import assetsRoutes from "./assets.js"
import campaignsRoutes from "./campaigns.js"
import contactsRoutes from "./contacts.js"
import hrRoutes from "./hr.js"
import menuRoutes from "./menu.js"
import warehouseRoutes from "./warehouse.js"
import disputesRoutes from "./disputes.js"
import journalRoutes from "./journal.js"
import tasksRoutes from "./tasks.js"
import mealsRoutes from "./meals.js"
import { MEALS_ADMIN_URL } from "@/config/mealsAdmin.js";

const routes = [
  // ADD THESE ROUTES BACK:
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: () => import("@/views/auth/AccessDenied.vue"),
    meta: { public: true },
  },
  {
    path: "/no-access",
    name: "NoAccess",
    component: () => import("@/views/auth/NoAccess.vue"),
    meta: {
      public: true,
      standalone: true, // Add this flag
    },
  },
  {
    path: "/",
    name: "Root",
    component: Login,
    meta: { public: true },
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: { public: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("@/views/auth/ForgotPassword.vue"),
    meta: { public: true },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("@/views/auth/ResetPassword.vue"),
    meta: { public: true },
  },
  {
    path: "/activate-account",
    name: "ActivateAccount",
    component: () => import("@/views/auth/ActivateAccount.vue"),
    meta: { public: true },
  },
  {
    path: "/force-password-change",
    name: "ForcePasswordChange",
    component: () => import("@/views/auth/ForcePasswordChange.vue"),
    meta: { requiresAuth: true, skipPasswordCheck: true },
  },
  {
    path: "/home",
    name: "Home",
    component: Home,
    meta: { requiresAuth: true },
  },
  {
    // The old Accounting landing page is retired (2026-09-10): nothing linked
    // to it since the sidebar reorg, and Home's "Data entry" / "Data review"
    // sections now cover everything it listed (its cards had gone stale —
    // "Cost → GL", no Delivery / Direct Upload / Month End).
    path: "/accounting",
    redirect: "/home",
  },
  {
    path: "/accounting/delivery",
    name: "Delivery",
    component: () => import("@/views/accounting/DeliveryPage.vue"),
    meta: { requiresAuth: true, app: "warehouse" },
  },
  {
    path: "/accounting/items",
    name: "Items",
    component: () => import("@/views/accounting/ItemsPage.vue"),
    meta: { requiresAuth: true, app: "warehouse" },
  },
  {
    path: "/accounting/costs-gl",
    name: "CostsGL",
    // GL reporting/operational — gated on the "gl" app (admin).
    component: () => import("@/views/accounting/CostsGLPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, app: "gl" },
  },
  {
    // The 8 closing steps as one guided screen — replaces the eight separate
    // menu entries, which said what the steps were but never where the month
    // had got to.
    path: "/accounting/month-end",
    name: "MonthEnd",
    component: () => import("@/views/accounting/MonthEndView.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, app: "gl" },
  },
  {
    path: "/accounting/settlements",
    name: "Settlements",
    component: () => import("@/views/accounting/SettlementsPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, app: "gl" },
    // Cheques & bank opens on the cheques in circulation (Maurizio, 2026-10-17). The bare URL
    // lands there; with ?section= (Payables / Receivables buttons) or ?from=month-end it stays.
    beforeEnter: (to) => (Object.keys(to.query).length ? true : { path: "/accounting/settlements/cheques", replace: true }),
  },
  {
    // Only the cheques-in-circulation table of the Settlements page (same component, focused mode).
    path: "/accounting/settlements/cheques",
    name: "ChequesInCirculation",
    component: () => import("@/views/accounting/SettlementsPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, app: "gl", chequesOnly: true },
  },
  {
    path: "/marketing",
    name: "MarketingHome",
    component: () => import("@/views/marketing/MarketingHome.vue"),
    // Released under the "platforms" app (was retired appadmin-only).
    meta: { requiresAuth: true, app: "platforms", feature: "marketing" },
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/views/settings/SettingsHome.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/account",
    name: "Account",
    component: () => import("@/views/account/AccountHome.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/settings/users",
    name: "UsersManagement",
    component: () => import("@/views/settings/UsersManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/audit-logs",
    name: "AuditLogs",
    component: () => import("@/views/settings/AuditLogs.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    // Platform × brand × place with dates and store ids — the single source
    // of truth (migration 155, 2026-09-23). The two screens it replaced
    // redirect here so old bookmarks still land.
    path: "/settings/outlets",
    name: "OutletsSettings",
    component: () => import("@/views/settings/OutletsSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  { path: "/settings/restaurant-mappings", redirect: "/settings/outlets" },
  { path: "/settings/business-rules", redirect: "/settings/outlets" },
  // {
  //   path: "/settings/platform-credentials",
  //   name: "PlatformCredentials",
  //   component: () => import("@/views/settings/PlatformCredentials.vue"),
  //   meta: { requiresAuth: true, requiresAppAdmin: true },
  // },
  {
    // What each platform is expected to produce per month (invoices, daily
    // costs). Lifted off the operator's coverage screen: it is configuration,
    // and appadmin-only like the rest of Settings.
    path: "/settings/platform-cost-coverage",
    name: "PlatformCostCoverageSettings",
    component: () => import("@/views/settings/PlatformCostCoverageSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/options",
    name: "OptionsManagement",
    component: () => import("@/views/settings/OptionsManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/platform-credentials",
    name: "PlatformCredentials",
    component: () => import("@/views/settings/PlatformCredentials.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/assets",
    name: "AssetSettings",
    component: () => import("@/views/settings/AssetSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/modules",
    name: "ModulesSettings",
    component: () => import("@/views/settings/ModulesSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/tags",
    name: "TagsManagement",
    component: () => import("@/views/settings/TagsManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/tasks",
    name: "TasksSettings",
    component: () => import("@/views/settings/TasksSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true, feature: "tasks" },
  },
  {
    path: "/settings/account-managers",
    name: "AccountManagersManagement",
    component: () => import("@/views/settings/AccountManagersManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true, feature: "reviews" },
  },
  {
    path: "/settings/address-book",
    name: "AddressBookSettings",
    component: () => import("@/views/settings/AddressBookSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/reviews-admin",
    name: "ReviewsAdmin",
    component: () => import("@/views/reviews/ReviewsAdmin.vue"),
    // Released under the "platforms" app (admin).
    meta: { requiresAuth: true, requiresAdmin: true, app: "platforms", feature: "reviews" },
  },
  // API Interface route removed — replaced by Export API JSON on Documentation page
  {
    path: "/settings/docs",
    name: "Documentation",
    component: () => import("@/views/settings/DocumentationPage.vue"),
    // Reference guides — open to all admins (not just appadmin) so operators like Iqbal can read them.
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  // 404 - Catch-all route - MUST BE LAST
  {
    path: "/:catchAll(.*)*",
    name: "NotFound",
    component: NotFound,
    meta: { public: true },
  },
];

// Marketing service (reviews, campaigns, disputes) released under the unified
// "platforms" app — override each group's legacy app code with "platforms".
const platformsApp = (rs) => rs.map((r) => ({ ...r, meta: { ...r.meta, app: "platforms" } }));

const router = createRouter({
  history: createWebHistory(),
  routes: routes.concat(salesRoutes, platformsApp(reviewsRoutes), assetsRoutes, platformsApp(campaignsRoutes), contactsRoutes, hrRoutes, menuRoutes, warehouseRoutes, platformsApp(disputesRoutes), journalRoutes, tasksRoutes, mealsRoutes),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Navigation guard for authentication and app access
// Returns the redirect target, or nothing to let the navigation through —
// vue-router 5 deprecates the next() callback (2026-09-28).
router.beforeEach(async (to) => {
  const token = localStorage.getItem("authToken");
  const userStr = localStorage.getItem("user");
  const passwordChangeRequired = localStorage.getItem("passwordChangeRequired") === "true";
  let user = {};
  let allowedApps = [];

  try {
    user = userStr ? JSON.parse(userStr) : {};
    const apps = user.allowed_apps;
    if (Array.isArray(apps)) {
      allowedApps = apps;
    } else if (apps) {
      allowedApps = JSON.parse(apps);
    }
  } catch (e) {
    console.error("Error parsing user:", e);
  }

  // If authenticated user visits Login page (or the bare "/"), redirect to
  // the appropriate home. Any token qualifies: the old `allowedApps.length > 0`
  // condition left an appadmin — whose grant list is empty by design, the
  // role bypasses grants — staring at the login form inside the app shell
  // (2026-09-10). A user with no grants still ends on NoAccess: the /home
  // guard below sends them there.
  if ((to.name === "Login" || to.name === "Root") && token) {
    // Check if password change is required first
    if (passwordChangeRequired) {
      return { name: "ForcePasswordChange" };
    }
    // Resume at the last visited page only when the user's preference allows it.
    // The setting lives in user_preferences (server) and is mirrored to
    // localStorage as "pref_resumeLastPage" by useUserPreferences — read that
    // mirror synchronously here (the guard runs before async prefs load).
    // Default true preserves the prior always-resume behavior; the Account page
    // exposes a toggle to land on Home instead, plus a Reset-to-Home button.
    let resumeLastPage = true;
    try {
      const raw = localStorage.getItem("pref_resumeLastPage");
      if (raw !== null) resumeLastPage = JSON.parse(raw) === true;
    } catch { /* malformed — keep default */ }

    // A last page under /meals dates from before Fusion Eat moved to its own
    // site (16 Oct 2026): resuming it would send the person away from this app
    // to the admin site's login, so it is forgotten instead.
    let lastPath = localStorage.getItem("lastPath");
    if (lastPath && /^\/meals(\/|$)/.test(lastPath)) {
      localStorage.removeItem("lastPath");
      lastPath = null;
    }
    if (resumeLastPage && lastPath && !["/login", "/", "/home"].includes(lastPath)) {
      return { path: lastPath };
    }
    return { path: "/home" };
  }

  // Skip auth check for public routes
  if (to.meta.public) {
    // console.log("Skipping auth check for public route:", to.name);
    return;
  }

  // Feature-flagged routes: if the feature is off, behave as if the page doesn't
  // exist (lets us deploy code dark and flip it on without a rebuild).
  // The server's module switches: started on the first signed-in navigation (the
  // sidebar and Home update when they arrive); a flagged route waits for them.
  if (token) {
    const flagsLoading = ensureFeatureFlags();
    if (to.meta.feature) await flagsLoading;
  }
  if (to.meta.feature && !isFeatureEnabled(to.meta.feature)) {
    return { name: "Home" };
  }

  // Route requires authentication but user is not authenticated
  if (to.meta.requiresAuth && !token) {
    if (to.path && to.path !== "/login" && to.path !== "/") {
      localStorage.setItem("lastPath", to.path);
    }
    return { name: "Login" };
  }

  // User is authenticated - check access
  if (token) {
    // Check if password change is required (unless on the ForcePasswordChange page or it has skipPasswordCheck)
    if (passwordChangeRequired && !to.meta.skipPasswordCheck && to.name !== "ForcePasswordChange") {
      return { name: "ForcePasswordChange" };
    }

    // appadmin has full access to everything
    if (user.role === "appadmin") {
      // Only check requiresAppAdmin routes - appadmin can access them
      // For all other routes, appadmin has full access
      return;
    }

    // Check app admin access (non-appadmin users denied)
    if (to.meta.requiresAppAdmin) {
      return { name: "AccessDenied", query: { reason: "appadmin" } };
    }

    // Check admin access (admin or appadmin roles)
    if (to.meta.requiresAdmin && !["admin", "appadmin"].includes(user.role)) {
      return { name: "AccessDenied", query: { reason: "admin" } };
    }

    // For regular users, check if they have access to ANY app
    if (allowedApps.length === 0) {
      if (to.name !== "NoAccess") {
        return { name: "NoAccess" };
      }
    }

    // Fusion Eat has its own site (16 Oct 2026): someone whose only app is
    // "meals" has nothing here, so they go there instead of an empty Home
    if (allowedApps.length > 0 && allowedApps.every((a) => a === "meals")) {
      window.location.assign(MEALS_ADMIN_URL);
      return false;
    }

    // Check specific app access if route has app requirement
    // meta.app can be a string or an array (user needs at least one match)
    const requiredApp = to.meta.app;
    if (requiredApp && (
      Array.isArray(requiredApp)
        ? !requiredApp.some((a) => allowedApps.includes(a))
        : !allowedApps.includes(requiredApp)
    )) {
      // Show the first required app name (for display purposes)
      const appLabel = Array.isArray(requiredApp) ? requiredApp[0] : requiredApp;
      return { name: "AccessDenied", query: { reason: "app", app: appLabel } };
    }
  }

  // All checks passed: returning nothing lets the navigation through.
});

// Save last visited authenticated page (for post-login redirect after tab close)
router.afterEach((to) => {
  if (to.meta.requiresAuth && !to.meta.skipPasswordCheck) {
    localStorage.setItem("lastPath", to.path);
  }
});

export default router;
